import { get, isFinite } from 'lodash-es';
import { getShortenUrl } from 'site-modules/shared/utils/url-shortener-client';
import { VEHICLE_SELECT_MODE_MAP, SHARED_UTM_PARAMS } from 'site-modules/shared/constants/price-checker';

/**
 * validate shared deal data
 * @param {object} data
 * @return {boolean}
 */
export const isSharedDataValid = data => {
  const maxFairPrice = parseFloat(get(data, 'maxFairPrice', null));
  const maxGoodPrice = parseFloat(get(data, 'maxGoodPrice', null));
  const maxGreatPrice = parseFloat(get(data, 'maxGreatPrice', null));
  const userDealerPrice = parseFloat(get(data, 'userDealerPrice', null));
  const userMsrp = parseFloat(get(data, 'userMsrp', null));
  const mode = get(data, 'mode', null);
  const styleId = get(data, 'styleId', null);
  const vin = get(data, 'vin', null);
  const zipCode = get(data, 'zipCode', null);
  const year = parseFloat(get(data, 'year', null));
  const make = get(data, 'make', null);
  const model = get(data, 'model', null);

  const utmSource = get(data, SHARED_UTM_PARAMS.utmSource.param, null);
  const utmMedium = get(data, SHARED_UTM_PARAMS.utmMedium.param, null);
  const utmTargetId = get(data, SHARED_UTM_PARAMS.utmTargetId.param, null);
  const utmCampaign = get(data, SHARED_UTM_PARAMS.utmCampaign.param, null);
  const utmAccount = get(data, SHARED_UTM_PARAMS.utmAccount.param, null);

  if (
    !zipCode ||
    !isFinite(maxFairPrice) ||
    !isFinite(maxGoodPrice) ||
    !isFinite(maxGreatPrice) ||
    !isFinite(userDealerPrice) ||
    !isFinite(userMsrp) ||
    !(mode === VEHICLE_SELECT_MODE_MAP.MMY || mode === VEHICLE_SELECT_MODE_MAP.VIN) ||
    !(
      utmSource === SHARED_UTM_PARAMS.utmSource.value &&
      utmMedium === SHARED_UTM_PARAMS.utmMedium.value &&
      utmTargetId !== null &&
      utmCampaign === SHARED_UTM_PARAMS.utmCampaign.value &&
      utmAccount === SHARED_UTM_PARAMS.utmAccount.value
    )
  ) {
    return false;
  }

  if (mode === VEHICLE_SELECT_MODE_MAP.MMY && !(year && make && model && styleId)) {
    return false;
  }

  return !(mode === VEHICLE_SELECT_MODE_MAP.VIN && !vin);
};

/**
 * Create Shorten Url for Price Checker
 * @param {object} sharedData
 * @param {boolean} isVinChecker
 * @return {string}
 */
export const getPriceCheckerShortenUrl = async (sharedData, isVinChecker) => {
  const maxFairPrice = get(sharedData, 'tmvChartData.maxFairPrice', null);
  const maxGoodPrice = get(sharedData, 'tmvChartData.maxGoodPrice', null);
  const maxGreatPrice = get(sharedData, 'tmvChartData.maxGreatPrice', null);
  const userDealerPrice = get(sharedData, 'userDealerPrice', null);
  const userMsrp = get(sharedData, 'userMsrp', null);
  const styleId = get(sharedData, 'styleId', null);
  const vin = get(sharedData, 'vin', null);
  const zipCode = get(sharedData, 'zipCode', null);
  const year = get(sharedData, 'year', null);
  const make = get(sharedData, 'make', null);
  const model = get(sharedData, 'model', null);
  const visitorId = get(sharedData, 'visitorId', null);

  const parameters = {
    zipCode,
    mode: isVinChecker ? VEHICLE_SELECT_MODE_MAP.VIN : VEHICLE_SELECT_MODE_MAP.MMY,
    maxFairPrice,
    maxGoodPrice,
    maxGreatPrice,
    userDealerPrice,
    userMsrp,

    [SHARED_UTM_PARAMS.utmSource.param]: SHARED_UTM_PARAMS.utmSource.value,
    [SHARED_UTM_PARAMS.utmMedium.param]: SHARED_UTM_PARAMS.utmMedium.value,
    [SHARED_UTM_PARAMS.utmTargetId.param]: visitorId,
    [SHARED_UTM_PARAMS.utmCampaign.param]: SHARED_UTM_PARAMS.utmCampaign.value,
    [SHARED_UTM_PARAMS.utmAccount.param]: SHARED_UTM_PARAMS.utmAccount.value,
  };

  if (isVinChecker) {
    parameters.vin = vin;
  } else {
    parameters.year = year;
    parameters.make = make;
    parameters.model = model;
    parameters.styleId = styleId;
  }

  // validation
  if (!isSharedDataValid(parameters)) {
    return null;
  }

  const queryStr = Object.keys(parameters)
    .map(key => `${key}=${encodeURIComponent(parameters[key])}`)
    .join('&');

  const url = `${window.location.origin}${window.location.pathname}?${queryStr}`;

  try {
    const { shortUrl } = await getShortenUrl({ url, ttl: '30d' });
    return shortUrl;
  } catch {
    return null;
  }
};

/**
 * Get data from shorten Url
 * @param {object} sharedData
 * @return {object}
 */
export const getParamsForSharedDeal = query => {
  // validation
  if (!isSharedDataValid(query)) {
    return null;
  }

  const maxFairPrice = Number(get(query, 'maxFairPrice'));
  const maxGoodPrice = Number(get(query, 'maxGoodPrice'));
  const maxGreatPrice = Number(get(query, 'maxGreatPrice'));
  const userDealerPrice = Number(get(query, 'userDealerPrice'));
  const userMsrp = Number(get(query, 'userMsrp'));

  const { mode, styleId, vin, zipCode } = query;

  const params = {
    zipCode,
    maxFairPrice,
    maxGoodPrice,
    maxGreatPrice,
    userDealerPrice,
    userMsrp,
    styleId,
    mode,
  };

  if (mode === VEHICLE_SELECT_MODE_MAP.VIN) {
    params.vin = vin;
    return params;
  }

  return params;
};
